import type {ImageField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import {useLinkResolver} from 'prismicio'
import type {ButtonDocument} from 'prismicio-types'
import {ButtonV2} from 'src/common/ButtonV2'
import {Icon, type IconType} from 'src/common/Icon'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'

type BackgroundMode = 'Dark mode' | 'Light mode grey' | 'Light mode white'

const BACKGROUND_CLASS: Record<BackgroundMode, string> = {
  'Dark mode': 'bg-green-dark',
  'Light mode grey': 'bg-grey-100',
  'Light mode white': 'bg-moss-white',
}

export type BadgeIconName = 'Plus' | 'Sparkle' | 'Check' | 'Lock' | 'Insights'
export type BadgeIconColor = 'Green' | 'Purple'

const BADGE_ICON_NAME: Record<BadgeIconName, IconType> = {
  // Bold variant chosen deliberately: the thin default 'plus' glyph's ~1px stroke disappears at this small a size
  Plus: 'plus-bold',
  Sparkle: 'sparkle',
  Check: 'check',
  Lock: 'lock',
  Insights: 'insights',
}

const BADGE_ICON_COLOR_CLASS: Record<BadgeIconColor, string> = {
  Green: 'bg-green-neon',
  Purple: 'bg-moss-purple',
}

// Glyph color per circle color, chosen for contrast: black on the light neon green, white on the darker purple
const BADGE_ICON_GLYPH_CLASS: Record<BadgeIconColor, string> = {
  Green: 'text-green-dark',
  Purple: 'text-moss-white',
}

export type StatCard = {
  label: string
  amount: string
  percentage: string
  trend: 'Up' | 'Down'
}

export type CentralMediaSectionUIProps = {
  backgroundImage: ImageField
  backgroundMode: BackgroundMode | null
  badgeIcon: BadgeIconName | null
  badgeIconColor: BadgeIconColor | null
  badgeText: string
  heading: string
  headingAccent: string
  description: string
  buttonData: ButtonDocument['data'] | null
  mainImage: ImageField
  chatQuestion: string
  statCards: StatCard[]
  pageUid: string
  paddingTop?: string | null
  paddingBottom?: string | null
}

export const CentralMediaSection = ({
  backgroundImage,
  backgroundMode,
  badgeIcon,
  badgeIconColor,
  badgeText,
  heading,
  headingAccent,
  description,
  buttonData,
  mainImage,
  chatQuestion,
  statCards,
  pageUid,
  paddingTop,
  paddingBottom,
}: CentralMediaSectionUIProps): JSX.Element => {
  const {linkResolver} = useLinkResolver()
  const mode = backgroundMode ?? 'Light mode white'
  const paddingTopClass = PADDING_TOP_CLASS[paddingTop ?? DEFAULT_PADDING] ?? ''
  const paddingBottomClass = PADDING_BOTTOM_CLASS[paddingBottom ?? DEFAULT_PADDING] ?? ''
  const hasBackgroundImage = isFilled.image(backgroundImage)
  // A photo backdrop always needs light, readable text over its dark overlay, regardless of the selected mode
  const isDark = mode === 'Dark mode' || hasBackgroundImage
  const hasBadge = Boolean(badgeText)
  const hasChatQuestion = Boolean(chatQuestion)
  const hasStatCards = statCards.length > 0
  const hasMediaBox = isFilled.image(mainImage)
  const hasOverlayContent = hasChatQuestion || hasStatCards

  const overlayContent = hasOverlayContent && (
    <>
      {hasChatQuestion && (
        <div className="flex w-full max-w-3xl items-center justify-between gap-4 overflow-hidden rounded-full border border-moss-white/15 bg-moss-white/20 py-2 pl-6 pr-2 backdrop-blur-xl md:py-3 md:pl-8 lg:py-3 lg:pl-10 lg:pr-7">
          <p className="text-14 font-medium text-moss-white md:text-20 lg:text-28">{chatQuestion}</p>
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-moss-white/30 md:size-12 lg:size-16">
            <Icon name="right-arrow" className="size-4 text-moss-white md:size-5 lg:size-8" />
          </span>
        </div>
      )}
      {hasStatCards && (
        <div className="mt-8 grid w-full max-w-3xl grid-cols-3 gap-2 md:gap-4">
          {statCards.map((card, index) => (
            <div
              key={index}
              className="flex flex-col items-center gap-3 rounded border border-moss-white/15 bg-moss-white/15 p-3 text-center backdrop-blur-xl md:items-stretch md:gap-5 md:p-5 md:text-left"
            >
              <p className="truncate text-10 font-semibold uppercase tracking-[0.6px] text-moss-white md:text-13 md:tracking-[0.78px]">
                {card.label}
              </p>
              <div className="flex flex-col items-center gap-1 md:flex-row md:items-center md:justify-between">
                <p className="truncate text-14 font-semibold text-moss-white md:text-18">{card.amount}</p>
                <span className="flex shrink-0 items-center gap-0.5 text-14 font-semibold text-moss-white/70 md:text-18">
                  {card.percentage}
                  <Icon name="arrow-diagonal-filled" className={cn('size-3', {'rotate-90': card.trend === 'Down'})} />
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )

  return (
    <section
      data-slice-type="central_media_section"
      data-slice-variation="default"
      data-nav-dark={isDark}
      className={cn('relative overflow-hidden', BACKGROUND_CLASS[mode], {
        'min-h-[600px] md:min-h-[720px] lg:min-h-[800px]': hasBackgroundImage,
      })}
    >
      {hasBackgroundImage && (
        <>
          <PrismicNextImage field={backgroundImage} fill className="object-cover object-top" fallbackAlt="" />
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-b from-transparent to-green-dark"
          />
        </>
      )}
      <div
        className={cn(
          'relative z-10 flex flex-col items-center px-5 text-center md:px-10 lg:px-[120px]',
          hasMediaBox ? 'gap-16 md:gap-20 lg:gap-24' : 'gap-12',
          paddingTopClass,
          paddingBottomClass,
        )}
      >
        <div className="flex flex-col items-center gap-6">
          {hasBadge && (
            <div className="flex items-center gap-2 rounded-full px-4 py-2">
              {badgeIcon && (
                <span
                  className={cn(
                    'flex size-6 shrink-0 items-center justify-center rounded-full',
                    BADGE_ICON_COLOR_CLASS[badgeIconColor ?? 'Green'],
                  )}
                >
                  <Icon
                    name={BADGE_ICON_NAME[badgeIcon]}
                    className={cn('size-4', BADGE_ICON_GLYPH_CLASS[badgeIconColor ?? 'Green'])}
                  />
                </span>
              )}
              {badgeText && (
                <p
                  className={cn('text-12 font-semibold uppercase tracking-[0.78px]', {
                    'text-moss-white': isDark,
                    'text-moss-black': !isDark,
                  })}
                >
                  {badgeText}
                </p>
              )}
            </div>
          )}
          {(heading || headingAccent) && (
            <h2 className="flex flex-col text-40 leading-[42px] tracking-[-0.4px] md:leading-tight lg:text-64 lg:leading-[64px] lg:tracking-[-0.64px]">
              {heading && (
                <span
                  className={cn({
                    'text-moss-white': isDark,
                    'text-moss-black': !isDark,
                  })}
                >
                  {heading}
                </span>
              )}
              {headingAccent && (
                <span
                  className={cn({
                    'text-moss-white/70': isDark,
                    'text-moss-black/60': !isDark,
                  })}
                >
                  {headingAccent}
                </span>
              )}
            </h2>
          )}
          {description && (
            <p
              className={cn('max-w-[535px] text-18 leading-6', {
                'text-moss-white': isDark,
                'text-moss-grey-400': !isDark,
              })}
            >
              {description}
            </p>
          )}
          {buttonData?.label && isFilled.link(buttonData.link) && (
            <ButtonV2
              variant="primary"
              href={linkResolver(buttonData.link, 'central-media-section')}
              trackingData={{
                location: 'central-media-section',
                dataType: 'primary-button',
                pageName: pageUid,
                mktgId: buttonData.marketing_id ?? '',
                label: buttonData.label,
              }}
            >
              {buttonData.label}
            </ButtonV2>
          )}
        </div>
        {hasMediaBox ? (
          <div className="relative w-full max-w-[1200px] overflow-hidden rounded-xl">
            {overlayContent ? (
              <>
                <PrismicNextImage field={mainImage} fill className="object-cover" fallbackAlt="" />
                <div className="relative z-10 flex flex-col items-center gap-4 p-4 md:px-8 md:py-12">
                  {overlayContent}
                </div>
              </>
            ) : (
              <PrismicNextImage field={mainImage} className="h-auto w-full object-cover" fallbackAlt="" />
            )}
          </div>
        ) : (
          overlayContent && (
            <div className="flex w-full max-w-[1200px] flex-col items-center gap-4">{overlayContent}</div>
          )
        )}
      </div>
    </section>
  )
}
